import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider.vue';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import { ConfirmationModalConfig } from '@shared/interfaces/confirmation-modal-config';
import { setProp } from '@shared/helpers/operators';
import { faMinusSquare, faPlusSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import AddWalletMovementModal from '@modules/wallet/components/modals/add-wallet-movement-modal/add-wallet-movement-modal.vue';

library.add(faPlusSquare, faMinusSquare);
@Component({
  name: 'bpc-contact-form',
  components: {
    'bpc-std-form': StdForm,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-add-wallet-movement-modal': AddWalletMovementModal,
    'font-awesome-icon': FontAwesomeIcon,
  },
})
export default class ContactForm extends Vue {
  $refs: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    stdForm: InstanceType<typeof StdForm> & {
      saveEntity(): void;
      formModel: any;
      searchEntity(): void;
      extraData: string | AnyEntity;
      formProvider: EntityFormProvider;
    };
    deleteMediaFileConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
  };
  @Prop(String) readonly entity: string;
  @Prop(String) readonly configFileName: string;
  @Prop([String, Number]) readonly entityId: string | number;
  @Prop([Object]) readonly value: AnyEntity;
  @Prop({ type: String, default: 'default' }) formConfigName: string;
  @Prop({ type: Boolean, default: true }) readonly disabled: boolean;
  @Prop(Object) readonly extraData: { externalFormModel?: AnyEntity };

  public showAddMovementWalletModal: boolean = false;
  public walletMovementType: 'credit' | 'debit' = 'credit';
  formKey = 0;

  public get externalFormModel() {
    return this.extraData?.externalFormModel;
  }

  public formModel: AnyEntity = {};

  onInputCustomEvent(event) {
    const eventName = event.eventName;
    switch (eventName) {
      case 'selectedCityFromPostalCode':
        // When updating the zip code field, update the city field
        setProp(this.$refs.stdForm?.formModel?.contactDetail, 'city', event.value);
        break;
      case 'selectedCountryFromPostalCode':
        // When updating the zip code field, update the country field
        setProp(this.$refs.stdForm?.formModel?.contactDetail, 'country', event.value.toLowerCase());
        break;
      default:
        break;
    }
  }

  mounted() {
    this.$root.$on('changeType', val => {
      this.$refs.stdForm.formModel.contactIndividual = null;
    });
    this.$root.$on('updateContact', event => {
      this.$refs.stdForm.formModel.to_invoice = event.to_invoice === 1;
      this.$refs.stdForm.formModel.when_to_invoice = event.when_to_invoice_value;
    });
    // Set the start date and end date according to the period when changing
    this.$root.$on('updateDates', event => {
      setProp(this.$refs.stdForm?.formModel, 'start_date', event.start_date);
      setProp(this.$refs.stdForm?.formModel, 'end_date', event.end_date);
    });

    /*
     * When updating the zip code field and if a center setting exist with a rule to assign a price category based on the postal code,
     * assign the category corresponding to the postal code.
     */
    this.$root.$on('updatePriceCategoryBasedOnPostalCode', event => {
      const defaultPriceCategoryBasedOnPostalCode = this.$store.getters[
        'centerSettings/defaultPriceCategoryBasedOnPostalCode'
      ];
      if (defaultPriceCategoryBasedOnPostalCode) {
        const postalCodesForDefaultPriceCategory = this.$store.getters[
          'centerSettings/postalCodesForDefaultPriceCategory'
        ];
        postalCodesForDefaultPriceCategory.forEach(postalCode => {
          if (postalCode === event) {
            // Retreive price category if the posal code corresponds to a price category
            const priceCategoryRoute = `${this.$bpcApiRouteHelper.getEntityPath(
              'price-categories',
            )}/${defaultPriceCategoryBasedOnPostalCode}`;
            this.$bpcApi.get(priceCategoryRoute).subscribe(
              res => {
                this.$refs.stdForm.formModel.priceCategory = res.data;
                this.$refs.stdForm.formModel.verified = false;
              },
              error => {
                console.log(error);
              },
            );
          } else {
            this.$refs.stdForm.formModel.priceCategory =
              this.$store.getters['centerSettings/defaultPriceCategory'] ?? null;
            this.$refs.stdForm.formModel.verified = true;
          }
        });
      }
    });
  }

  destroyed() {
    this.$root.$off('changeType');
    this.$root.$off('updateContact');
    this.$root.$off('updatePriceCategoryBasedOnPostalCode');
    this.$root.$off('updateDates');
  }

  onFormSaved(e) {
    this.$emit('entity-saved', e);
  }
  onSavingEntity(event) {
    this.$emit('saving-entity', event);
  }
  onErrorSavingEntity(event) {
    this.$emit('entity-error-saving', event);
  }
  deleteMediaFile(mediaId) {
    this.$emit('delete-media-file', { mediaId: mediaId, entityId: this.entityId });
  }
  saveEntity() {
    this.$refs.stdForm.saveEntity();
  }
  getDeleteMediaFileConfirmationModalConfig(): ConfirmationModalConfig {
    return {
      id: 'media-file-delete-modal',
      title: this.$t('global.modals.media-delete.title') as string,
      body: this.$t('global.modals.media-delete.body') as string,
      cancelText: this.$t('global.buttons.cancel') as string,
      acceptText: this.$t('global.buttons.permanent_delete') as string,
      acceptButtonVariant: 'danger',
    };
  }

  onSuccessMediaFileDelete() {
    this.$refs.deleteMediaFileConfirmationModal.onCloseModal();
  }

  showDeleteMediaFileModal(mediaId: string) {
    this.$refs.deleteMediaFileConfirmationModal.onOpenModal(mediaId);
  }

  onOpenWalletModal(movementType: 'credit' | 'debit' = 'credit') {
    this.walletMovementType = movementType;
    this.showAddMovementWalletModal = true;
  }

  onCloseWalletModal() {
    this.showAddMovementWalletModal = false;
  }

  onWalletMovementAdded() {
    // Force re-render the form. Maybe there is a better way to do this.
    this.formKey += 1;
  }

  @Watch('formModel', { immediate: true, deep: true })
  onFormModelChange() {
    this.$emit('input', this.formModel);
  }
}
